import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import ThemeToggle from "@/components/ThemeToggle";

/**
 * MobileHeader — Header ringkas untuk mobile, pengganti hero section besar.
 * Hanya tampil di mobile (md:hidden).
 *
 * Props:
 * - title: string — Judul halaman
 * - showBack?: boolean — Tampilkan tombol back (default: false)
 * - onBack?: () => void — Custom back handler (default: navigate(-1))
 * - rightActions?: ReactNode — Elemen custom di kanan (selain ThemeToggle)
 * - transparent?: boolean — Gunakan background transparan (misal di atas hero)
 */
export default function MobileHeader({
  title = "Al-Qur'an",
  showBack = false,
  onBack,
  rightActions,
  transparent = false,
}) {
  const navigate = useNavigate();

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  return (
    <header
      className={`
        fixed top-4 left-4 right-4 z-50 md:hidden flex items-center justify-between pointer-events-none transition-all duration-300
      `}
    >
      {/* Left — Immersive Floating Back Button */}
      <div className="pointer-events-auto">
        {showBack ? (
          <button
            onClick={handleBack}
            aria-label="Kembali"
            className="w-10 h-10 flex items-center justify-center rounded-full bg-white/80 dark:bg-slate-900/80 backdrop-blur-2xl saturate-150 border border-black/5 dark:border-white/10 shadow-[0_8px_30px_rgb(0,0,0,0.08)] text-emerald-600 dark:text-emerald-400 hover:scale-105 active:scale-95 transition-all"
          >
            <ChevronLeft size={24} strokeWidth={2.5} className="-ml-0.5" />
          </button>
        ) : (
          /* Logo dot dekoratif - Floating */
          <Link to="/" aria-label="Beranda" className="active:scale-90 block hover:scale-105 transition-all">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center shadow-[0_8px_30px_rgb(16,185,129,0.3)] border border-white/20">
              <span className="text-white text-sm font-black">ق</span>
            </div>
          </Link>
        )}
      </div>

      {/* Right — Immersive Floating Actions */}
      <div className="pointer-events-auto flex items-center gap-2">
        {rightActions && (
          <div className="flex items-center gap-1 bg-white/80 dark:bg-slate-900/80 backdrop-blur-2xl saturate-150 rounded-full border border-black/5 dark:border-white/10 shadow-[0_8px_30px_rgb(0,0,0,0.08)] px-1 py-1">
            {rightActions}
          </div>
        )}
        <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-2xl saturate-150 rounded-full border border-black/5 dark:border-white/10 shadow-[0_8px_30px_rgb(0,0,0,0.08)]">
          <ThemeToggle />
        </div>
      </div>
    </header>
  );
}
